import { Injectable } from '@angular/core';

type ListType = 'ol' | 'ul';

@Injectable({ providedIn: 'root' })
export class AiChatHtmlFormatterService {
    convertToHtml(rawText: string, hasStructuredSources = false): string {
        if (!rawText?.trim()) return '';

        const text = hasStructuredSources ? this.removeEmbeddedSources(rawText) : rawText;
        return this.renderMarkdown(text);
    }

    private renderMarkdown(rawText: string): string {
        const lines = rawText.replace(/\r\n?/g, '\n').split('\n');
        const output: string[] = [];
        const paragraph: string[] = [];
        let listType: ListType | null = null;
        let nestedListOpen = false;

        const closeList = (): void => {
            if (!listType) return;
            if (listType === 'ol' && nestedListOpen) output.push('</li></ul>');
            output.push(`</li></${listType}>`);
            listType = null;
            nestedListOpen = false;
        };
        const closeParagraph = (): void => {
            if (!paragraph.length) return;
            output.push(`<p>${paragraph.join('<br>')}</p>`);
            paragraph.length = 0;
        };

        for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) {
                closeParagraph();
                closeList();
                continue;
            }

            const heading = line.match(/^(#{1,6})\s+(.+)$/);
            if (heading) {
                closeParagraph();
                closeList();
                const level = heading[1].length <= 2 ? 'h3' : 'h4';
                output.push(`<${level}>${this.renderInline(heading[2])}</${level}>`);
                continue;
            }
            if (/^#{1,6}\s*$/.test(line)) continue;

            if (/^(---|\*\*\*|___)$/.test(line)) {
                closeParagraph();
                closeList();
                output.push('<hr>');
                continue;
            }

            const orderedItem = line.match(/^\d+[.)]\s+(.+)$/);
            if (orderedItem) {
                closeParagraph();
                if (listType === 'ul') closeList();
                if (listType === 'ol') {
                    output.push(nestedListOpen ? '</li></ul></li><li>' : '</li><li>');
                    nestedListOpen = false;
                } else {
                    output.push('<ol class="ai-chat-list"><li>');
                    listType = 'ol';
                }
                output.push(this.renderInline(orderedItem[1]));
                continue;
            }

            const unorderedItem = line.match(/^[-*+]\s+(.+)$/);
            if (unorderedItem) {
                closeParagraph();
                if (listType === 'ol') {
                    if (nestedListOpen) {
                        output.push('</li><li>');
                    } else {
                        output.push('<ul class="ai-chat-sublist"><li>');
                        nestedListOpen = true;
                    }
                } else if (listType === 'ul') {
                    output.push('</li><li>');
                } else {
                    output.push('<ul class="ai-chat-list"><li>');
                    listType = 'ul';
                }
                output.push(this.renderInline(unorderedItem[1]));
                continue;
            }

            const quote = line.match(/^>\s*(.+)$/);
            if (quote) {
                closeParagraph();
                closeList();
                output.push(`<blockquote>${this.renderInline(quote[1])}</blockquote>`);
                continue;
            }

            closeList();
            paragraph.push(this.renderInline(line));
        }

        closeParagraph();
        closeList();
        return output.join('');
    }

    private renderInline(value: string): string {
        let text = this.escapeHtml(value);
        text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, label: string, href: string) => {
            const safeHref = this.safeHref(href);
            return safeHref
                ? `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`
                : label;
        });
        text = text.replace(/`([^`]+)`/g, '<code>$1</code>');
        text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        text = text.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
        return text;
    }

    private removeEmbeddedSources(value: string): string {
        const sourceHeading = /\n\s*(?:#{1,6}\s*)?sources\s*:\s*(?:\n|$)/i;
        const match = sourceHeading.exec(value);
        return match ? value.slice(0, match.index).trimEnd() : value;
    }

    private safeHref(value: string): string | null {
        const href = value.trim();
        return /^(https?:\/\/|mailto:|\/|#)/i.test(href) ? href : null;
    }

    private escapeHtml(value: string): string {
        return value.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
